// Storage Service for offline-first photo handling with R2 sync
// Photos are saved locally first, then synced to Cloudflare R2 when online

import { photoDb, db } from '../db/database'
import { photoApi, convertToJPEG } from './apiService'

// Track sync status with timeout protection
let isSyncing = false
let syncStartTime = null
let syncListeners = []
const SYNC_TIMEOUT_MS = 60000 // 60 second timeout for stuck syncs

/**
 * Register a listener for sync status changes
 * @param {Function} callback - Called with { syncing, pending, error }
 */
export function onSyncStatusChange(callback) {
  syncListeners.push(callback)
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback)
  }
}

function notifySyncStatus(status) {
  syncListeners.forEach(cb => cb(status))
}

/**
 * Save photo locally first (offline-first approach)
 * @param {Object} photoData - Photo data including surveyId, categoryCode, blob
 * @returns {Object} Local photo record
 */
export async function savePhotoLocally(photoData) {
  const { surveyId, categoryCode, blob, thumbnailBlob, notes = '' } = photoData

  // Convert blobs to base64 for IndexedDB storage
  const imageData = blob ? await blobToBase64(blob) : null
  const thumbnailData = thumbnailBlob ? await blobToBase64(thumbnailBlob) : null

  // Save to IndexedDB
  const localPhoto = await photoDb.add({
    surveyId,
    categoryCode,
    imageData,
    thumbnailData,
    notes,
    analyseWithAI: true,
    uploaded: false,
    r2Key: null,
    remoteId: null  // Will be set after successful upload
  })

  return localPhoto
}

/**
 * Upload a single photo to R2
 * @param {Object} photo - Local photo object from IndexedDB
 * @returns {Object} Upload result with remote photo data
 */
export async function uploadToR2(photo) {
  try {
    // CRITICAL: Check if photo already uploaded to prevent duplicates
    // If photo has remoteId, it's ALREADY on the server - NEVER upload again!
    // This prevents duplicates when cached photos have remoteId but uploaded=false
    if (photo.remoteId || photo.r2Key || photo.uploaded) {
      console.log(`[uploadToR2] Photo ${photo.id} already uploaded, skipping:`, {
        uploaded: photo.uploaded,
        remoteId: photo.remoteId,
        r2Key: photo.r2Key,
        surveyId: photo.surveyId,
        categoryCode: photo.categoryCode
      })
      return {
        success: true,
        localId: photo.id,
        remotePhoto: { id: photo.remoteId, r2Key: photo.r2Key },
        skipped: true
      }
    }

    console.log(`[uploadToR2] Uploading photo ${photo.id}:`, {
      uploaded: photo.uploaded,
      remoteId: photo.remoteId,
      r2Key: photo.r2Key,
      surveyId: photo.surveyId,
      categoryCode: photo.categoryCode
    })

    // Convert base64 back to blob for upload
    // Handle case where imageData might already be a Blob (corrupted data)
    let imageBlob
    if (photo.imageData instanceof Blob) {
      console.warn('Photo imageData is already a Blob, using directly')
      imageBlob = photo.imageData
    } else if (typeof photo.imageData === 'string') {
      imageBlob = await base64ToBlob(photo.imageData)
    } else {
      // Log detailed info about the invalid data
      console.error('Invalid imageData:', {
        type: typeof photo.imageData,
        isNull: photo.imageData === null,
        isUndefined: photo.imageData === undefined,
        constructor: photo.imageData?.constructor?.name,
        keys: photo.imageData ? Object.keys(photo.imageData) : [],
        photoId: photo.id,
        surveyId: photo.surveyId,
        categoryCode: photo.categoryCode
      })
      throw new Error('Invalid imageData format: expected base64 string or Blob, got ' + typeof photo.imageData + ' (constructor: ' + (photo.imageData?.constructor?.name || 'unknown') + ')')
    }

    let thumbnailBlob = null
    if (photo.thumbnailData) {
      if (photo.thumbnailData instanceof Blob) {
        console.warn('Photo thumbnailData is already a Blob, using directly')
        thumbnailBlob = photo.thumbnailData
      } else if (typeof photo.thumbnailData === 'string') {
        thumbnailBlob = await base64ToBlob(photo.thumbnailData)
      } else {
        console.warn('Invalid thumbnailData format, skipping thumbnail')
      }
    }

    // Convert to JPEG for PDF compatibility
    let jpegBlob = imageBlob
    if (imageBlob.type !== 'image/jpeg') {
      jpegBlob = await convertToJPEG(imageBlob)
    }

    let jpegThumb = thumbnailBlob
    if (thumbnailBlob && thumbnailBlob.type !== 'image/jpeg') {
      jpegThumb = await convertToJPEG(thumbnailBlob, 0.8)
    }

    // Upload via API
    const remotePhoto = await photoApi.upload(
      photo.surveyId,
      photo.categoryCode,
      jpegBlob,
      jpegThumb
    )

    // Update notes if any
    if (photo.notes) {
      await photoApi.update(remotePhoto.id, { notes: photo.notes })
    }

    // Mark as uploaded in IndexedDB
    await photoDb.markUploaded(photo.id, remotePhoto.r2Key)

    // Store remote ID for reference
    await db.photos.update(photo.id, { remoteId: remotePhoto.id })

    return {
      success: true,
      localId: photo.id,
      remotePhoto
    }
  } catch (error) {
    console.error('Failed to upload photo to R2:', error)
    return {
      success: false,
      localId: photo.id,
      error: error.message
    }
  }
}

/**
 * Sync all pending photos to R2
 * @param {Function} onProgress - Progress callback (current, total)
 * @returns {Object} Sync results
 */
export async function syncPhotosToR2(onProgress, forceReset = false) {
  // Check for stuck sync (timeout protection)
  if (isSyncing && !forceReset) {
    const elapsed = syncStartTime ? Date.now() - syncStartTime : 0
    if (elapsed < SYNC_TIMEOUT_MS) {
      return { success: false, message: 'Sync already in progress' }
    }
    // Sync has been running too long - reset it
    console.warn('Sync timeout detected, resetting sync state')
    isSyncing = false
  }

  if (!navigator.onLine) {
    return { success: false, message: 'No internet connection' }
  }

  isSyncing = true
  syncStartTime = Date.now()
  notifySyncStatus({ syncing: true, pending: 0, error: null })

  try {
    // Get all pending photos from IndexedDB
    const allPhotos = await db.photos.toArray()
    // Only include photos that aren't uploaded AND don't have remoteId/r2Key
    const pendingPhotos = allPhotos.filter(p => !p.uploaded && !p.remoteId && !p.r2Key)

    console.log(`[syncPhotosToR2] Total photos: ${allPhotos.length}, Pending: ${pendingPhotos.length}`)
    console.log('[syncPhotosToR2] All photos:', allPhotos.map(p => ({
      id: p.id,
      surveyId: p.surveyId,
      category: p.categoryCode,
      uploaded: p.uploaded,
      hasRemoteId: !!p.remoteId,
      hasR2Key: !!p.r2Key
    })))

    if (pendingPhotos.length === 0) {
      isSyncing = false
      syncStartTime = null
      notifySyncStatus({ syncing: false, pending: 0, error: null })
      return { success: true, synced: 0, message: 'All photos already synced' }
    }

    console.log('[syncPhotosToR2] Pending photos to upload:', pendingPhotos.map(p => ({
      id: p.id,
      category: p.categoryCode,
      uploaded: p.uploaded,
      remoteId: p.remoteId,
      r2Key: p.r2Key
    })))

    notifySyncStatus({ syncing: true, pending: pendingPhotos.length, error: null })

    let synced = 0
    let failed = 0
    const errors = []

    for (let i = 0; i < pendingPhotos.length; i++) {
      const photo = pendingPhotos[i]

      if (onProgress) {
        onProgress(i + 1, pendingPhotos.length)
      }

      const result = await uploadToR2(photo)

      if (result.success) {
        synced++
      } else {
        failed++
        errors.push({ photoId: photo.id, error: result.error })
      }

      notifySyncStatus({
        syncing: true,
        pending: pendingPhotos.length - i - 1,
        error: null
      })
    }

    isSyncing = false
    syncStartTime = null
    notifySyncStatus({ syncing: false, pending: failed, error: errors.length > 0 ? errors[0].error : null })

    return {
      success: failed === 0,
      synced,
      failed,
      errors,
      message: failed === 0
        ? `Successfully synced ${synced} photos`
        : `Synced ${synced} photos, ${failed} failed`
    }
  } catch (error) {
    isSyncing = false
    syncStartTime = null
    notifySyncStatus({ syncing: false, pending: 0, error: error.message })
    return {
      success: false,
      error: error.message,
      message: 'Sync failed: ' + error.message
    }
  }
}

/**
 * Get count of pending (unsynced) photos
 * @returns {number} Count of pending photos
 */
export async function getPendingCount() {
  const allPhotos = await db.photos.toArray()
  // Only count photos that aren't uploaded AND don't have remoteId/r2Key
  // (photos with remoteId/r2Key are already on the server, even if uploaded=false)
  return allPhotos.filter(p => !p.uploaded && !p.remoteId && !p.r2Key).length
}

/**
 * Get all photos for a survey from local DB
 * @param {string} surveyId - Survey ID
 * @returns {Array} Photos array
 */
export async function getPhotosForSurvey(surveyId) {
  return photoDb.getBySurvey(surveyId)
}

/**
 * Delete a photo (both local and remote)
 * @param {number} localId - Local photo ID
 * @param {string} remoteId - Remote photo ID (if uploaded)
 */
export async function deletePhoto(localId, remoteId) {
  // Delete from R2 if uploaded
  if (remoteId) {
    try {
      await photoApi.delete(remoteId)
    } catch (error) {
      console.warn('Failed to delete remote photo:', error)
    }
  }

  // Delete from IndexedDB
  await photoDb.delete(localId)
}

// Helper: Convert blob to base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Helper: Convert base64 to blob
function base64ToBlob(base64) {
  return new Promise((resolve, reject) => {
    if (!base64 || typeof base64 !== 'string') {
      reject(new Error('Invalid base64 data: expected string, got ' + typeof base64))
      return
    }

    if (!base64.includes(',')) {
      reject(new Error('Invalid base64 format: missing data URI prefix'))
      return
    }

    const [header, data] = base64.split(',')
    const mimeMatch = header.match(/data:([^;]+)/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'

    const binary = atob(data)
    const array = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i)
    }

    resolve(new Blob([array], { type: mime }))
  })
}

/**
 * Cache a remote photo to local IndexedDB
 * Fetches the image from R2 and stores it locally for offline use
 * @param {Object} photo - Remote photo object with imageUrl
 * @param {number} surveyId - Local survey ID
 * @returns {Object} Photo object with imageData added
 */
export async function cacheRemotePhoto(photo, surveyId) {
  try {
    // Check if already cached locally - try multiple strategies to avoid duplicates
    let existing = await db.photos.where('remoteId').equals(photo.id).first()

    // If found by remoteId and has imageData, return it
    if (existing && existing.imageData) {
      console.log(`Photo ${photo.id} already cached (remoteId match)`)
      return {
        ...photo,
        imageData: existing.imageData,
        thumbnailData: existing.thumbnailData,
        localId: existing.id
      }
    }

    // Check for duplicates by r2Key (uploaded photos will have same r2Key)
    if (photo.r2Key) {
      const duplicateCheck = await db.photos
        .where('surveyId')
        .equals(surveyId)
        .filter(p => p.categoryCode === photo.categoryCode && p.r2Key === photo.r2Key)
        .first()

      if (duplicateCheck) {
        console.log(`Photo ${photo.id} already cached (r2Key match), updating remoteId`)
        // Update with remoteId and ensure it's marked as uploaded
        await db.photos.update(duplicateCheck.id, {
          remoteId: photo.id,
          uploaded: true,
          r2Key: photo.r2Key
        })
        return {
          ...photo,
          imageData: duplicateCheck.imageData || null,
          thumbnailData: duplicateCheck.thumbnailData || null,
          localId: duplicateCheck.id
        }
      }
    }

    // Check for recently added photos (within 10 seconds) that might be pending upload
    // This catches photos that were just added locally but haven't synced yet
    const recentPhotos = await db.photos
      .where('surveyId')
      .equals(surveyId)
      .filter(p => p.categoryCode === photo.categoryCode && !p.uploaded && !p.remoteId)
      .toArray()

    if (recentPhotos.length > 0) {
      // Sort by most recent first
      recentPhotos.sort((a, b) => new Date(b.capturedAt) - new Date(a.capturedAt))
      const mostRecent = recentPhotos[0]

      // If captured within last 10 seconds, likely the same photo - link them
      const timeDiff = Date.now() - new Date(mostRecent.capturedAt).getTime()
      if (timeDiff < 10000) {
        console.log(`Linking pending photo ${mostRecent.id} to remote photo ${photo.id}`)
        await db.photos.update(mostRecent.id, {
          remoteId: photo.id,
          uploaded: true,
          r2Key: photo.r2Key
        })
        return {
          ...photo,
          imageData: mostRecent.imageData,
          thumbnailData: mostRecent.thumbnailData,
          localId: mostRecent.id
        }
      }
    }

    // Fetch image from R2
    const response = await fetch(photo.imageUrl)
    if (!response.ok) {
      console.warn(`Failed to fetch photo ${photo.id}: ${response.status}`)
      return photo
    }

    const blob = await response.blob()
    const imageData = await blobToBase64(blob)

    // Fetch thumbnail if available
    let thumbnailData = null
    if (photo.thumbnailUrl) {
      try {
        const thumbResponse = await fetch(photo.thumbnailUrl)
        if (thumbResponse.ok) {
          const thumbBlob = await thumbResponse.blob()
          thumbnailData = await blobToBase64(thumbBlob)
        }
      } catch (e) {
        console.warn('Failed to fetch thumbnail:', e)
      }
    }

    // If we found an existing photo but it didn't have imageData, update it
    if (existing) {
      console.log(`Updating existing photo ${existing.id} with cached data`)
      await db.photos.update(existing.id, {
        imageData,
        thumbnailData,
        uploaded: true,
        r2Key: photo.r2Key || existing.r2Key,
        remoteId: photo.id
      })

      return {
        ...photo,
        imageData,
        thumbnailData,
        localId: existing.id
      }
    }

    // Otherwise create new cached photo entry
    console.log(`Caching new photo ${photo.id} for survey ${surveyId}`)
    const localPhoto = await photoDb.add({
      surveyId,
      categoryCode: photo.categoryCode,
      imageData,
      thumbnailData,
      notes: photo.notes || '',
      analyseWithAI: photo.analyseWithAI !== false,
      uploaded: true, // Already on R2
      r2Key: photo.r2Key,
      remoteId: photo.id
    })

    return {
      ...photo,
      imageData,
      thumbnailData,
      localId: localPhoto.id
    }
  } catch (error) {
    console.error('Failed to cache photo:', error)
    return photo // Return original without cache
  }
}

/**
 * Check if online and R2 available
 * @returns {boolean} Availability status
 */
export function isR2Available() {
  return navigator.onLine
}

/**
 * Get current sync status
 */
export function getSyncStatus() {
  return { syncing: isSyncing }
}

/**
 * Diagnostic: Get all pending photos with their data types
 * Useful for identifying corrupted photos
 * @returns {Array} Array of pending photos with diagnostic info
 */
export async function diagnosePendingPhotos() {
  const allPhotos = await db.photos.toArray()
  const pendingPhotos = allPhotos.filter(p => !p.uploaded)

  return pendingPhotos.map(photo => ({
    id: photo.id,
    surveyId: photo.surveyId,
    categoryCode: photo.categoryCode,
    imageDataType: typeof photo.imageData,
    imageDataIsBlob: photo.imageData instanceof Blob,
    imageDataIsNull: photo.imageData === null,
    imageDataConstructor: photo.imageData?.constructor?.name,
    thumbnailDataType: typeof photo.thumbnailData,
    capturedAt: photo.capturedAt
  }))
}

/**
 * Delete a corrupted photo from IndexedDB
 * Use this to clean up photos that can't be synced
 * @param {number} photoId - Local photo ID
 */
export async function deleteCorruptedPhoto(photoId) {
  await photoDb.delete(photoId)
  console.log(`Deleted corrupted photo ${photoId} from IndexedDB`)
}

/**
 * Emergency function to clear all pending (not uploaded) photos
 * WARNING: This will delete all photos that haven't been uploaded to R2
 * @returns {Object} Deletion report
 */
export async function clearAllPendingPhotos() {
  const allPhotos = await db.photos.toArray()
  const pendingPhotos = allPhotos.filter(p => !p.uploaded)

  for (const photo of pendingPhotos) {
    await db.photos.delete(photo.id)
  }

  console.log(`Deleted ${pendingPhotos.length} pending photos from IndexedDB`)

  return {
    deleted: pendingPhotos.length,
    remaining: allPhotos.length - pendingPhotos.length
  }
}

/**
 * Find and report duplicate photos in IndexedDB
 * Duplicates are identified by:
 * 1. Same remoteId (exact duplicates from server)
 * 2. Same surveyId + categoryCode + r2Key (uploaded copies)
 * 3. Same surveyId + categoryCode + capturedAt (likely duplicates)
 * 4. Same surveyId + categoryCode with matching image hash
 * @returns {Object} Duplicate analysis report
 */
export async function findDuplicatePhotos() {
  const allPhotos = await db.photos.toArray()
  const duplicates = []
  const byRemoteId = {}
  const byR2Key = {}
  const bySurveyCategory = {}
  const byCapturedTime = {}

  for (const photo of allPhotos) {
    // Track by remoteId
    if (photo.remoteId) {
      if (!byRemoteId[photo.remoteId]) {
        byRemoteId[photo.remoteId] = []
      }
      byRemoteId[photo.remoteId].push(photo)
    }

    // Track by r2Key
    if (photo.r2Key) {
      if (!byR2Key[photo.r2Key]) {
        byR2Key[photo.r2Key] = []
      }
      byR2Key[photo.r2Key].push(photo)
    }

    // Track by survey + category + capturedAt (within 1 second)
    if (photo.capturedAt) {
      const capturedTime = new Date(photo.capturedAt).getTime()
      // Round to nearest second to catch near-duplicates
      const roundedTime = Math.floor(capturedTime / 1000)
      const timeKey = `${photo.surveyId}:${photo.categoryCode}:${roundedTime}`
      if (!byCapturedTime[timeKey]) {
        byCapturedTime[timeKey] = []
      }
      byCapturedTime[timeKey].push(photo)
    }

    // Track by survey + category for analysis
    const key = `${photo.surveyId}:${photo.categoryCode}`
    if (!bySurveyCategory[key]) {
      bySurveyCategory[key] = []
    }
    bySurveyCategory[key].push(photo)
  }

  // Find duplicates by remoteId
  for (const [remoteId, photos] of Object.entries(byRemoteId)) {
    if (photos.length > 1) {
      duplicates.push({
        type: 'remoteId',
        key: remoteId,
        count: photos.length,
        photos: photos.map(p => ({
          id: p.id,
          surveyId: p.surveyId,
          categoryCode: p.categoryCode,
          uploaded: p.uploaded,
          r2Key: p.r2Key,
          remoteId: p.remoteId,
          capturedAt: p.capturedAt
        }))
      })
    }
  }

  // Find duplicates by r2Key
  for (const [r2Key, photos] of Object.entries(byR2Key)) {
    if (photos.length > 1) {
      duplicates.push({
        type: 'r2Key',
        key: r2Key,
        count: photos.length,
        photos: photos.map(p => ({
          id: p.id,
          surveyId: p.surveyId,
          categoryCode: p.categoryCode,
          uploaded: p.uploaded,
          r2Key: p.r2Key,
          remoteId: p.remoteId,
          capturedAt: p.capturedAt
        }))
      })
    }
  }

  // Find duplicates by capturedAt time (within same second)
  for (const [timeKey, photos] of Object.entries(byCapturedTime)) {
    if (photos.length > 1) {
      // Skip if already found by remoteId or r2Key
      const hasRemoteIdDup = photos.every(p => p.remoteId) &&
        duplicates.some(d => d.type === 'remoteId' && d.photos.some(dp => dp.id === photos[0].id))
      const hasR2KeyDup = photos.every(p => p.r2Key) &&
        duplicates.some(d => d.type === 'r2Key' && d.photos.some(dp => dp.id === photos[0].id))

      if (!hasRemoteIdDup && !hasR2KeyDup) {
        duplicates.push({
          type: 'capturedAt',
          key: timeKey,
          count: photos.length,
          photos: photos.map(p => ({
            id: p.id,
            surveyId: p.surveyId,
            categoryCode: p.categoryCode,
            uploaded: p.uploaded,
            r2Key: p.r2Key,
            remoteId: p.remoteId,
            capturedAt: p.capturedAt
          }))
        })
      }
    }
  }

  // Check for surveys with excessive photos per category (likely duplicates)
  const suspiciousCategories = []
  for (const [key, photos] of Object.entries(bySurveyCategory)) {
    if (photos.length > 10) {
      suspiciousCategories.push({
        key,
        count: photos.length,
        uploaded: photos.filter(p => p.uploaded).length,
        pending: photos.filter(p => !p.uploaded).length
      })
    }
  }

  return {
    totalPhotos: allPhotos.length,
    uploadedPhotos: allPhotos.filter(p => p.uploaded).length,
    pendingPhotos: allPhotos.filter(p => !p.uploaded).length,
    duplicateGroups: duplicates.length,
    duplicates,
    suspiciousCategories
  }
}

/**
 * Clean up duplicate photos from IndexedDB
 * Keeps the best version of each photo and removes duplicates
 * @param {boolean} dryRun - If true, only reports what would be deleted
 * @returns {Object} Cleanup report
 */
export async function cleanupDuplicatePhotos(dryRun = true) {
  const analysis = await findDuplicatePhotos()
  const toDelete = []

  for (const group of analysis.duplicates) {
    // Sort photos: prefer uploaded ones with imageData, then by newest ID
    const sorted = [...group.photos].sort((a, b) => {
      // Prefer uploaded photos
      if (a.uploaded && !b.uploaded) return -1
      if (!a.uploaded && b.uploaded) return 1
      // Prefer photos with remoteId
      if (a.remoteId && !b.remoteId) return -1
      if (!a.remoteId && b.remoteId) return 1
      // Prefer newer (higher ID)
      return b.id - a.id
    })

    // Keep the first one, delete the rest
    const toKeep = sorted[0]
    const toRemove = sorted.slice(1)

    for (const photo of toRemove) {
      toDelete.push({
        id: photo.id,
        surveyId: photo.surveyId,
        categoryCode: photo.categoryCode,
        uploaded: photo.uploaded,
        reason: `Duplicate of photo ${toKeep.id} (keeping ${toKeep.uploaded ? 'uploaded' : 'pending'} version)`
      })
    }
  }

  if (!dryRun) {
    for (const photo of toDelete) {
      await db.photos.delete(photo.id)
      console.log(`Deleted duplicate photo ${photo.id}: ${photo.reason}`)
    }
  }

  return {
    analysis,
    wouldDelete: toDelete.length,
    deleted: dryRun ? 0 : toDelete.length,
    dryRun,
    details: toDelete
  }
}

export default {
  savePhotoLocally,
  uploadToR2,
  syncPhotosToR2,
  getPendingCount,
  getPhotosForSurvey,
  deletePhoto,
  onSyncStatusChange,
  isR2Available,
  getSyncStatus,
  cacheRemotePhoto,
  diagnosePendingPhotos,
  deleteCorruptedPhoto,
  clearAllPendingPhotos,
  findDuplicatePhotos,
  cleanupDuplicatePhotos
}
